import { isAgronaveModel } from 'app/utils/device-model.utils';

/** Shared presentation defaults for task cards. */
export const DEFAULT_DISPLAY_TIMEZONE = 'UTC';
const MS_PER_MINUTE = 60000;
const MINUTES_PER_HOUR = 60;
const MAX_OFFSET_HOURS = 14;

function parseTimezoneOffset(value: unknown): number | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  if (/^(UTC|GMT|Z)$/i.test(value)) {
    return 0;
  }
  const match = value.match(
    /^(?:(?:GMT|UTC)\s*)?([+-])(\d{1,2})(?::?(\d{2}))?$/i,
  );
  if (!match) {
    return undefined;
  }
  const hours = Number(match[2]);
  const minutes = Number(match[3] || 0);
  if (
    hours > MAX_OFFSET_HOURS ||
    minutes >= MINUTES_PER_HOUR ||
    (hours === MAX_OFFSET_HOURS && minutes !== 0)
  ) {
    return undefined;
  }
  let offset = hours * MINUTES_PER_HOUR + minutes;
  if (match[1] === '-') {
    offset = -offset;
  }
  return offset;
}

export function validTaskInstant(
  value: unknown,
): string | number | Date | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    !(value instanceof Date)
  ) {
    return undefined;
  }
  if (!Number.isFinite(new Date(value).getTime())) {
    return undefined;
  }
  return value;
}

/**
 * Presentation-only compatibility for AGP dates written with the legacy offset.
 * @remarks Preserves the old PDF interpretation without modifying stored tasks.
 * ISOBUS startTime/stopTime and stageInfo already contain UTC and stay untouched.
 */
export function legacyAgpDisplayTask<
  T extends { trabalho?: any; etapa?: any[] },
>(task: T): T {
  const work = task.trabalho;
  if (
    !work ||
    work.temporalNormalizationVersion ||
    !isAgronaveModel(work.model)
  ) {
    return task;
  }
  const convert = (value: any) => {
    if (value === null || value === undefined || value === '') {
      return value;
    }
    const instant = new Date(value).getTime();
    if (!Number.isFinite(instant)) {
      return null;
    }
    let embeddedOffset: string;
    if (typeof value === 'string') {
      embeddedOffset = value.match(/(Z|[+-]\d{2}:?\d{2})$/i)?.[1];
    }
    const offset =
      parseTimezoneOffset(embeddedOffset) ??
      parseTimezoneOffset(work.timezone) ??
      0;
    return new Date(instant + offset * MS_PER_MINUTE).toISOString();
  };
  const bounds = (value: any) => ({
    ...value,
    timestamp: convert(value.timestamp),
    endTimestamp: convert(value.endTimestamp),
  });
  return {
    ...task,
    trabalho: { ...bounds(work), tal_timestamp: convert(work.tal_timestamp) },
    etapa: task.etapa?.map(bounds),
  };
}
